import React from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { AddUserForm } from "@/components/children/AddUserForm";
import { ChildList } from "@/components/children/ChildList";
import { PendingTransfersManagement } from "./PendingTransfersManagement";
import { getFamilyMembersByFamilyId, addChild, removeChild, updateChild, createInvitation, getTransfersByFamilyId, approveTransfer, rejectTransfer, getFamilyById } from "@/lib/firebase";
import { User, Transfer } from "@/types";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function ParentView() {
  const { currentUser } = useAuth();
  const [familyMembers, setFamilyMembers] = useState<User[]>([]);
  const [transfers, setTransfers] = useState<Transfer[]>([]);
  const [familyName, setFamilyName] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isTransferActionLoading, setIsTransferActionLoading] = useState(false);
  const [memberToRemove, setMemberToRemove] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    const fetchData = async () => {
      if (!currentUser?.familyId) return;
      
      setIsLoading(true);
      try {
        // Fetch family members, transfers and family name in parallel
        const [familyMembersData, transfersData, family] = await Promise.all([
          getFamilyMembersByFamilyId(currentUser.familyId),
          getTransfersByFamilyId(currentUser.familyId),
          getFamilyById(currentUser.familyId),
        ]);
        // Filter out the current user from the list
        const otherMembers = familyMembersData.filter(member => member.id !== currentUser.id);
        setFamilyMembers(otherMembers);
        setTransfers(transfersData as Transfer[]);
        if (family) setFamilyName((family as { name?: string }).name ?? "");
      } catch (error) {
        console.error("Error fetching data:", error);
        toast({
          title: "Error",
          description: "Failed to load data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [currentUser, toast]);

  const handleApproveTransfer = async (transferId: string) => {
    if (!currentUser?.id) return;
    
    setIsTransferActionLoading(true);
    try {
      await approveTransfer(transferId, currentUser.id);
      
      // Refresh transfers
      const transfersData = await getTransfersByFamilyId(currentUser.familyId!);
      setTransfers(transfersData as Transfer[]);
      
      toast({
        title: "Success",
        description: "Transfer approved successfully.",
      });
    } catch (error) {
      console.error("Error approving transfer:", error);
      toast({
        title: "Error",
        description: "Failed to approve transfer. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsTransferActionLoading(false);
    }
  };

  const handleRejectTransfer = async (transferId: string) => {
    if (!currentUser?.id) return;
    
    setIsTransferActionLoading(true);
    try {
      await rejectTransfer(transferId, currentUser.id);
      
      // Refresh transfers
      const transfersData = await getTransfersByFamilyId(currentUser.familyId!);
      setTransfers(transfersData as Transfer[]);
      
      toast({
        title: "Success",
        description: "Transfer rejected successfully.",
      });
    } catch (error) {
      console.error("Error rejecting transfer:", error);
      toast({
        title: "Error",
        description: "Failed to reject transfer. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsTransferActionLoading(false);
    }
  };

  const handleAddUser = async (firstName: string, lastName: string, username: string, role: 'child' | 'parent'): Promise<string> => {
    if (!currentUser?.familyId) throw new Error("No family ID");
    
    try {
      const userId = await addChild(
        currentUser.familyId,
        firstName,
        lastName,
        username,
        role
      );
      
      // Create invitation
      const inviteUrl = await createInvitation(username, userId);
      
      // Refresh the list of family members
      const familyMembersData = await getFamilyMembersByFamilyId(currentUser.familyId);
      const otherMembers = familyMembersData.filter(member => member.id !== currentUser.id);
      setFamilyMembers(otherMembers);
      
      return inviteUrl;
    } catch (error) {
      console.error("Error adding user:", error);
      toast({
        title: "Error",
        description: "Failed to add user. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleOpenRemoveDialog = (memberId: string) => {
    setMemberToRemove(memberId);
  };

  const handleRemoveMember = async () => {
    if (!memberToRemove) return;
    
    try {
      await removeChild(memberToRemove);
      
      // Update the local state
      setFamilyMembers(familyMembers.filter(member => member.id !== memberToRemove));
      
      toast({
        title: "Success",
        description: "Family member removed successfully.",
      });
    } catch (error) {
      console.error("Error removing family member:", error);
      toast({
        title: "Error",
        description: "Failed to remove family member. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMemberToRemove(null);
    }
  };

  const handleUpdateMember = async (memberId: string, firstName: string, lastName: string) => {
    try {
      await updateChild(memberId, firstName, lastName);
      
      // Update the local state
      setFamilyMembers(familyMembers.map(member => 
        member.id === memberId 
          ? { ...member, firstName, lastName } 
          : member
      ));
      
      toast({
        title: "Success",
        description: "Family member information updated successfully.",
      });
    } catch (error) {
      console.error("Error updating family member:", error);
      toast({
        title: "Error",
        description: "Failed to update family member information. Please try again.",
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">{familyName ? `${familyName} Family` : "Your Family Dashboard"}</h1>
          <p className="text-muted-foreground">
            Manage your family members and their accounts
          </p>
        </div>
        
        <AddUserForm onAddChild={handleAddUser} />
      </div>
      
      <div className="space-y-6">
        <PendingTransfersManagement
          transfers={transfers}
          familyMembers={[...familyMembers, currentUser!]}
          onApprove={handleApproveTransfer}
          onReject={handleRejectTransfer}
          isLoading={isTransferActionLoading}
        />
        
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-shekel-600"></div>
          </div>
        ) : (
          <ChildList 
            children={familyMembers} 
            onRemoveChild={handleOpenRemoveDialog} 
            onUpdateChild={handleUpdateMember}
          />
        )}
      </div>
      
      <AlertDialog open={!!memberToRemove} onOpenChange={() => setMemberToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this family member and all their transaction history.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveMember}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
